import { useState } from "react";
import { Pill } from "./Master";
import { SweetAlertShow } from "../../../master/Master";
import { studentTransportNFeeStatusChange } from "../../../../api/StudentDetails";
import Swal from "sweetalert2";

const STATUS_OPTIONS = [
  {
    value: "1",
    label: "Active",
    icon: "ri-checkbox-circle-line",
    color: "success",
  },
  {
    value: "2",
    label: "Inactive",
    icon: "ri-pause-circle-line",
    color: "secondary",
  },
  {
    value: "3",
    label: "Delete",
    icon: "ri-delete-bin-6-line",
    color: "danger",
  },
];

const transportStatusMeta = (status) => {
  switch (String(status)) {
    case "1":
      return {
        label: "Active",
        color: "success",
        icon: "ri-checkbox-circle-line",
      };

    case "2":
      return {
        label: "Inactive",
        color: "secondary",
        icon: "ri-pause-circle-line",
      };

    case "3":
      return {
        label: "Deleted",
        color: "danger",
        icon: "ri-delete-bin-6-line",
      };

    default:
      return {
        label: "Unknown",
        color: "secondary",
        icon: "ri-question-line",
      };
  }
};

const formatInputDate = (date) => {
  if (!date) return "";

  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  const d = new Date(date);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export default function TransportStatusModal({ entry, onClose, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(
    entry?.status ? String(entry.status) : "1",
  );
  const [reason, setReason] = useState(entry?.reason || "");
  const [endDate, setEndDate] = useState(formatInputDate(entry?.end_date));
  const [errors, setErrors] = useState({});

  const statusMeta = transportStatusMeta(entry?.status);
  const isDelete = status === "3";
  const isInactive = status === "2";
  const isReasonRequired = isInactive || isDelete;
  const canDelete = Number(entry?.total_paid || 0) === 0;

  const visibleStatusOptions = STATUS_OPTIONS.filter(
    (option) => option.value !== "3" || canDelete,
  );

  const validate = () => {
    const nextErrors = {};
    if (!status) {
      nextErrors.status = "Status is required.";
    }

    if (isInactive) {
      if (!endDate) {
        nextErrors.endDate = "End date is required.";
      }
      if (
        endDate &&
        entry?.joining_date &&
        endDate < formatInputDate(entry.joining_date)
      ) {
        nextErrors.endDate = "End date cannot be before joining date.";
      }
    }

    if (isReasonRequired && !reason.trim()) {
      nextErrors.reason = "Reason is required for this status.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const confirmDelete = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      title: "Delete Record?",
      html: `
          <p>
            This action will permanently delete
            the selected transport record.
          </p>

          <p>
            <strong>
              This action cannot be undone.
            </strong>
          </p>
        `,
      showCancelButton: true,
      confirmButtonText: "🗑️ Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      reverseButtons: true,
      focusCancel: true,
      allowOutsideClick: false,
      allowEscapeKey: true,
    });

    return isConfirmed;
  };

  const handleUpdateStatus = async () => {
    if (!validate()) {
      return;
    }

    if (isDelete) {
      const confirmed = await confirmDelete();
      if (!confirmed) {
        return;
      }
    }

    setSaving(true);

    try {
      const payload = {
        bus_student_id: entry?.id,
        bus_id: entry?.bus_id,
        student_id: entry?.student_id,
        status: status,
        reason: reason.trim() || null,
        end_date: isInactive ? endDate : null,
      };

      const response = await studentTransportNFeeStatusChange(payload);

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Successfully",
          response?.data?.message || "Transport status updated successfully.",
        );
        onUpdated?.(response.data);
        onClose?.();
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to update transport status.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Something went wrong!",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal d-block"
      style={{
        background: "rgba(0,0,0,0.5)",
      }}
      tabIndex={-1}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content rounded-4 border-0 overflow-hidden">
          <div className="modal-header border-0 pb-0">
            <div>
              <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                <i className="ri-bus-2-line text-primary"></i>
                Transport Status
              </h5>

              <div className="d-flex flex-wrap gap-2 mt-2">
                <Pill
                  icon="ri-bus-2-line"
                  color="primary"
                  label="Bus"
                  value={entry?.bus?.bus_number || "-"}
                />

                <Pill
                  icon="ri-map-pin-line"
                  color="success"
                  label="Stop"
                  value={entry?.stop?.stop_name || "-"}
                />

                <span
                  className={`badge rounded-pill bg-${statusMeta.color} d-inline-flex align-items-center gap-1 px-3 py-2`}
                >
                  <i className={statusMeta.icon}></i>

                  {statusMeta.label}
                </span>
              </div>
            </div>

            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={saving}
            ></button>
          </div>

          <div className="modal-body">
            <div className="row g-3 mb-3">
              <div className="col-12">
                <div className="p-3 rounded-3 bg-light border">
                  <p className="fw-semibold mb-2 d-flex align-items-center gap-1">
                    <i className="ri-information-line text-primary"></i>
                    What each status means
                  </p>

                  <ul className="list-unstyled mb-0 small">
                    <li className="d-flex align-items-start gap-2 mb-2">
                      <i className="ri-checkbox-circle-line text-success mt-1"></i>

                      <span>
                        <strong>Active</strong> – The bus route is currently
                        assigned and the student is using it.
                      </span>
                    </li>

                    <li className="d-flex align-items-start gap-2 mb-2">
                      <i className="ri-pause-circle-line text-secondary mt-1"></i>

                      <span>
                        <strong>Inactive</strong> – The route has ended or the
                        student is no longer using it. The record will remain in
                        history.
                      </span>
                    </li>

                    <li className="d-flex align-items-start gap-2">
                      <i className="ri-delete-bin-6-line text-danger mt-1"></i>

                      <span>
                        <strong>Delete</strong> – Permanently removes the
                        transport record.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="col-md-12">
                <label className="form-label fw-semibold d-flex align-items-center gap-1">
                  <i className="ri-price-tag-3-line text-warning"></i>
                  Status
                </label>

                <select
                  className={`form-select ${errors.status ? "is-invalid" : ""}`}
                  value={status}
                  disabled={saving}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    setStatus(newStatus);
                    setErrors((prev) => ({
                      ...prev,
                      status: undefined,
                      endDate: undefined,
                      reason: undefined,
                    }));
                  }}
                >
                  {visibleStatusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.status && (
                  <div className="invalid-feedback">{errors.status}</div>
                )}

                {!canDelete && (
                  <div className="form-text text-danger">
                    <i className="ri-information-line me-1"></i>
                    Delete is only available when total paid amount is 0.
                  </div>
                )}
              </div>

              {isInactive && (
                <div className="col-md-12">
                  <label className="form-label fw-semibold d-flex align-items-center gap-1">
                    <i className="ri-calendar-check-line text-primary"></i>
                    End Date
                    <span className="text-danger">*</span>
                  </label>

                  <input
                    type="date"
                    className={`form-control ${
                      errors.endDate ? "is-invalid" : ""
                    }`}
                    value={endDate}
                    min={formatInputDate(entry?.joining_date)}
                    disabled={saving}
                    onChange={(e) => {
                      setEndDate(e.target.value);

                      setErrors((prev) => ({
                        ...prev,
                        endDate: undefined,
                      }));
                    }}
                  />

                  {errors.endDate && (
                    <div className="invalid-feedback">{errors.endDate}</div>
                  )}

                  {entry?.joining_date && (
                    <div className="form-text">
                      <i className="ri-information-line me-1"></i>
                      Joining Date: {entry.joining_date}
                    </div>
                  )}
                </div>
              )}

              <div className="col-md-12">
                <label className="form-label fw-semibold d-flex align-items-center gap-1">
                  <i className="ri-message-2-line text-info"></i>
                  Reason
                  {isReasonRequired ? (
                    <span className="text-danger">*</span>
                  ) : (
                    <span className="text-muted fw-normal">(optional)</span>
                  )}
                </label>

                <textarea
                  className={`form-control ${
                    errors.reason ? "is-invalid" : ""
                  }`}
                  rows={3}
                  placeholder={
                    isInactive
                      ? "Enter reason for ending the transport service..."
                      : isDelete
                        ? "Enter reason for deleting this record..."
                        : "Add a note about this status change..."
                  }
                  value={reason}
                  disabled={saving}
                  onChange={(e) => {
                    setReason(e.target.value);

                    setErrors((prev) => ({
                      ...prev,
                      reason: undefined,
                    }));
                  }}
                ></textarea>

                {errors.reason && (
                  <div className="invalid-feedback">{errors.reason}</div>
                )}
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="p-3 rounded-3 bg-light border">
                  <small className="text-muted d-block">Joining Date</small>

                  <strong>{entry?.joining_date || "-"}</strong>
                </div>
              </div>

              <div className="col-md-4">
                <div className="p-3 rounded-3 bg-light border">
                  <small className="text-muted d-block">Total Fee</small>

                  <strong>
                    ₹
                    {Number(entry?.total_final_fee || 0).toLocaleString(
                      "en-IN",
                    )}
                  </strong>
                </div>
              </div>

              <div className="col-md-4">
                <div className="p-3 rounded-3 bg-light border">
                  <small className="text-muted d-block">Total Paid</small>

                  <strong className="text-success">
                    ₹{Number(entry?.total_paid || 0).toLocaleString("en-IN")}
                  </strong>
                </div>
              </div>
            </div>

            <div className="d-flex gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={saving}
                onClick={onClose}
              >
                <i className="ri-close-line me-1"></i>
                Cancel
              </button>

              <button
                type="button"
                className={`btn ${
                  isDelete
                    ? "btn-danger"
                    : isInactive
                      ? "btn-warning"
                      : "btn-primary"
                }`}
                onClick={handleUpdateStatus}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>

                    {isDelete ? "Deleting..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <i
                      className={
                        isDelete
                          ? "ri-delete-bin-6-line me-1"
                          : "ri-save-line me-1"
                      }
                    ></i>

                    {isDelete ? "Delete" : "Save Status"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
